import { ThemeEvents, CartAddEvent } from '@theme/events';

/**
 * "Gratis Muster" button: adds the sample variant matching the selected fabric
 * variant to the regular cart and enforces the per-cart sample limit.
 *
 * Markup contract: blocks/plixxo-muster-button.liquid
 */
class PlixxoMusterButton extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;
  /** @type {Record<string, number>} */
  #map = {};
  /** @type {string[]} sample variant ids currently in the cart */
  #inCart = [];
  #count = 0;
  #busy = false;
  #error = '';

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    try {
      this.#map = JSON.parse(this.querySelector('[data-muster-map]')?.textContent || '{}');
    } catch {
      this.#map = {};
    }

    this.button?.addEventListener('click', this.#handleClick, { signal });
    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });

    this.#refreshCart();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  get limit() {
    return Number(this.dataset.limit) || 6;
  }

  get button() {
    return this.querySelector('[data-muster-add]');
  }

  get sampleVariantId() {
    const id = this.#map[this.dataset.variantId || ''];
    return id ? String(id) : '';
  }

  /** @param {Event & {detail?: any}} event */
  #handleVariantUpdate = (event) => {
    const variant = event.detail?.resource;
    const productId = event.detail?.data?.productId;
    if (!variant?.id || (productId && productId !== this.dataset.productId)) return;

    this.dataset.variantId = String(variant.id);
    this.#error = '';
    this.#render();
  };

  /** @param {Event & {detail?: any}} event */
  #handleCartUpdate = (event) => {
    if (event.detail?.data?.source === 'plixxo-muster' && event.detail?.sourceId === this.sampleVariantId) {
      // Our own add: cart state was already refreshed before dispatching.
      return;
    }
    this.#refreshCart();
  };

  async #refreshCart() {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        signal: this.#controller?.signal,
      });
      const cart = await response.json();
      const lines = cart.items.filter(
        (/** @type {any} */ item) => String(item.product_id) === this.dataset.musterProductId
      );
      this.#inCart = lines.map((/** @type {any} */ item) => String(item.variant_id));
      this.#count = lines.reduce((/** @type {number} */ sum, /** @type {any} */ item) => sum + item.quantity, 0);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error(error);
    }
    this.#render();
  }

  #render() {
    const button = this.button;
    const label = this.querySelector('[data-muster-label]');
    const status = this.querySelector('[data-muster-status]');
    if (!button || !label || !status) return;

    const sampleId = this.sampleVariantId;
    const inCart = sampleId !== '' && this.#inCart.includes(sampleId);
    const limitReached = this.#count >= this.limit;

    let text = this.dataset.textAdd;
    let blocked = false;
    if (!sampleId) {
      text = this.dataset.textUnavailable;
      blocked = true;
    } else if (inCart) {
      text = this.dataset.textInCart;
    } else if (limitReached) {
      text = this.dataset.textLimit;
      blocked = true;
    }

    label.textContent = text || '';
    button.setAttribute('aria-disabled', String(blocked));
    button.setAttribute('aria-busy', String(this.#busy));

    const hint = this.#error
      ? this.#error
      : limitReached
        ? this.dataset.textLimitHint
        : (this.dataset.textRemaining || '').replace('[count]', String(this.#count));
    status.textContent = hint || '';
    status.classList.toggle('plixxo-muster__status--alert', Boolean(this.#error) || limitReached);
  }

  /**
   * Opens the sample sheet (snippets/plixxo-muster-sheet.liquid); falls back to the cart drawer.
   * @param {'added' | 'limit'} [state]
   */
  #openSheet(state) {
    if (customElements.get('plixxo-muster-sheet')) {
      document.dispatchEvent(new CustomEvent('plixxo-muster:open', { detail: { state } }));
    } else {
      /** @type {any} */ (document.querySelector('cart-drawer-component'))?.open?.();
    }
  }

  #handleClick = async () => {
    if (this.#busy) return;
    const sampleId = this.sampleVariantId;
    if (!sampleId) return;

    if (this.#inCart.includes(sampleId)) {
      this.#openSheet();
      return;
    }

    this.#busy = true;
    this.#error = '';
    this.#render();

    try {
      // Re-check against the live cart so other tabs or devices can't bypass the limit.
      await this.#refreshCart();
      if (this.#count >= this.limit) {
        this.#openSheet('limit');
        return;
      }

      const sectionIds = [...document.querySelectorAll('cart-items-component')]
        .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
        .filter(Boolean);

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(sampleId), quantity: 1 }],
          sections: sectionIds.join(','),
        }),
        signal: this.#controller?.signal,
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        this.#error = data.description || data.message || this.dataset.textError || '';
        return;
      }

      this.#inCart.push(sampleId);
      this.#count += 1;

      // Muster öffnen das Muster-Fenster statt des Warenkorb-Drawers (Horizon öffnet ihn sonst bei jedem cart:update).
      const drawer = customElements.get('plixxo-muster-sheet')
        ? document.querySelector('cart-drawer-component[auto-open]')
        : null;
      drawer?.removeAttribute('auto-open');
      this.dispatchEvent(
        new CartAddEvent({}, sampleId, {
          source: 'plixxo-muster',
          itemCount: 1,
          productId: this.dataset.musterProductId,
          variantId: sampleId,
          sections: data.sections,
        })
      );
      drawer?.setAttribute('auto-open', '');
      this.#openSheet('added');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error(error);
      this.#error = this.dataset.textError || '';
    } finally {
      this.#busy = false;
      this.#render();
    }
  };
}

if (!customElements.get('plixxo-muster-button')) {
  customElements.define('plixxo-muster-button', PlixxoMusterButton);
}
